import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link as MLink,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Stack,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AddIcon from '@mui/icons-material/Add';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { caseService } from '../../services/caseService';
import { config } from '../../config/environment';
import { formatDateTime } from '../../utils/date';
import {
  getFolderDisplayName,
  getMrrDeliveryFolderSuggestions,
  getCaseDeliveryFolderSuggestions,
  isAdditionalRecordsTag,
  isPriorCaseDeliveryTag,
  latestAdditionalRecordsTag,
} from './caseFileUtils';

export interface CaseFilesPanelProps {
  caseId: number;
  caseStatus: string;
  currentDueDate?: string | null;
  priorityBasedDueDate?: boolean;
  casePriority?: string;
  readOnly?: boolean;
  showDocumentReview?: boolean;
  showSync?: boolean;
  hasVendorAssignment?: boolean;
  hasClientAssignment?: boolean;
  navigateToReview: (fileId: number) => void;
  onToast: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
  onCaseReopened?: () => void;
}

const suggestedReopenDueDate = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + 15);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

interface CaseFileRow {
  id: number;
  name: string;
  original_name?: string;
  size_label?: string;
  created_at?: string;
  mime_type?: string;
  uploaded_by_user?: { name?: string };
}

const CaseFilesPanel: React.FC<CaseFilesPanelProps> = ({
  caseId,
  caseStatus,
  currentDueDate = null,
  priorityBasedDueDate = false,
  casePriority = 'normal',
  readOnly = false,
  showDocumentReview = false,
  showSync = false,
  hasVendorAssignment = false,
  hasClientAssignment = false,
  navigateToReview,
  onToast,
  onCaseReopened,
}) => {
  const [fileGroups, setFileGroups] = useState<Record<string, CaseFileRow[]>>({});
  const [tagDisplayNames, setTagDisplayNames] = useState<Record<string, string>>({});
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
  const [selectedAll, setSelectedAll] = useState(false);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadingFileName, setDownloadingFileName] = useState('');

  const [uploadOptions, setUploadOptions] = useState({
    deliveryComment: '',
    deliveryFolderName: '',
  });
  const [deliveryType, setDeliveryType] = useState<'mrr' | 'case' | null>(null);
  const [pendingDeliveryFiles, setPendingDeliveryFiles] = useState<File[]>([]);
  const [selectedExistingFileIds, setSelectedExistingFileIds] = useState<number[]>([]);
  const [allowAdditionalUploads, setAllowAdditionalUploads] = useState(false);
  // True only until the user picks a folder. While set, uploads create Additional {Mon YYYY}
  // instead of landing in whichever folder the list had auto-selected.
  const [additionalTargetIsAuto, setAdditionalTargetIsAuto] = useState(false);
  const additionalTargetIsAutoRef = useRef(false);
  const folderExplicitRef = useRef(false);
  const caseStatusLowerRef = useRef('');
  const [skipVendorReopen, setSkipVendorReopen] = useState(false);
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [reopenDueDate, setReopenDueDate] = useState('');
  const [reopening, setReopening] = useState(false);
  const [deliveryExpanded, setDeliveryExpanded] = useState(true);

  const deliveryFileInputRef = useRef<HTMLInputElement>(null);

  const [editingTag, setEditingTag] = useState<string | null>(null);
  const [newDisplayName, setNewDisplayName] = useState('');

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<{ id: number; name: string } | null>(null);

  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);

  const [hideFolderConfirmOpen, setHideFolderConfirmOpen] = useState(false);
  const [folderToHide, setFolderToHide] = useState<{ tag: string; name: string; fileCount: number } | null>(null);
  const [hidingFolder, setHidingFolder] = useState(false);

  const [sharePointDialogOpen, setSharePointDialogOpen] = useState(false);
  const [sharePointUrl, setSharePointUrl] = useState('');
  const [importingSharePoint, setImportingSharePoint] = useState(false);
  const [deliverySource, setDeliverySource] = useState<'files' | 'sharepoint'>('files');
  const [deliverySharePointUrl, setDeliverySharePointUrl] = useState('');

  const [dragOver, setDragOver] = useState(false);
  const [uploadMenuAnchor, setUploadMenuAnchor] = useState<null | HTMLElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const zipInputRef = useRef<HTMLInputElement>(null);

  const caseStatusLower = String(caseStatus || '').toLowerCase();
  caseStatusLowerRef.current = caseStatusLower;
  const isDelivered = caseStatusLower === 'delivered';
  const isMrrDelivered = caseStatusLower === 'mrr_delivered';
  const isClosed = caseStatusLower === 'closed';
  const isCompleted = caseStatusLower === 'completed';

  const canUpload =
    readOnly
      ? false
      : (!isDelivered && !isClosed && !isCompleted) || allowAdditionalUploads || isMrrDelivered;

  /** Delivered / closed / completed block uploads until reopen */
  const needsReopenToUpload = isDelivered || isClosed || isCompleted;

  /** MRR delivered allows uploads, but user may still want an Additional folder */
  const showOptionalAdditionalFolder = isMrrDelivered && !allowAdditionalUploads && !readOnly;

  const showReopenBanner = !readOnly && (needsReopenToUpload || showOptionalAdditionalFolder || allowAdditionalUploads);

  const deliveryFlagsApply =
    (!isDelivered && !isClosed && !isCompleted) || allowAdditionalUploads || isMrrDelivered;

  const groupNames = Object.keys(fileGroups);

  const hasAdditionalCycle = useMemo(
    () => groupNames.some((t) => isAdditionalRecordsTag(t)),
    [groupNames]
  );

  const existingFolderNames = useMemo(() => {
    const names = new Set<string>(groupNames);
    Object.values(tagDisplayNames).forEach((name) => {
      if (name) names.add(name);
    });
    return Array.from(names);
  }, [groupNames, tagDisplayNames]);

  const mrrFolderSuggestions = useMemo(
    () => getMrrDeliveryFolderSuggestions(hasAdditionalCycle, existingFolderNames),
    [hasAdditionalCycle, existingFolderNames]
  );

  const caseFolderSuggestions = useMemo(
    () => getCaseDeliveryFolderSuggestions(hasAdditionalCycle, existingFolderNames),
    [hasAdditionalCycle, existingFolderNames]
  );

  const useDeliveryTarget = deliveryType !== null;

  const activeDisplayName = activeTag
    ? getFolderDisplayName(activeTag, tagDisplayNames)
    : '';

  const usesAutoAdditionalFolder =
    allowAdditionalUploads && additionalTargetIsAuto && !useDeliveryTarget;

  const selectedUploadFolderLabel = useDeliveryTarget
    ? uploadOptions.deliveryFolderName.trim() || 'Delivery folder (required)'
    : activeDisplayName || (usesAutoAdditionalFolder ? 'Additional records (auto)' : '');

  const canUploadHere =
    canUpload && !readOnly && !deliveryType && (!!activeTag || allowAdditionalUploads);

  // Files from MRR delivery folders that can be selected for case delivery (avoiding re-upload)
  const mrrDeliveredFiles = useMemo(() => {
    const result: Array<CaseFileRow & { tag: string; tagDisplayName: string }> = [];
    Object.entries(fileGroups).forEach(([tag, files]) => {
      if (tag === 'MRR Delivery Records' || tag.includes('MRR Delivery Records')) {
        const displayName = tagDisplayNames[tag] || tag;
        files.forEach((f) => result.push({ ...f, tag, tagDisplayName: displayName }));
      }
    });
    return result;
  }, [fileGroups, tagDisplayNames]);

  const canSubmitDelivery =
    deliveryType !== null
    && !!uploadOptions.deliveryFolderName.trim()
    && (pendingDeliveryFiles.length > 0 || selectedExistingFileIds.length > 0)
    && !isUploading;

  const loadFiles = useCallback(async (preserveTag?: string | null) => {
    try {
      const filesRes = await caseService.listFiles(caseId);
      const groups = filesRes.data.groups || {};
      const names = filesRes.data.tag_display_names || {};
      const tags = Object.keys(groups);
      setFileGroups(groups);
      setTagDisplayNames(names);

      const status = String(caseStatusLowerRef.current || '');
      const priorCaseDelivery = tags.some((tag) => isPriorCaseDeliveryTag(tag));
      let reopenedDefault: string | null | undefined;
      if (
        !preserveTag
        && !folderExplicitRef.current
        && !additionalTargetIsAutoRef.current
        && status === 'open'
        && priorCaseDelivery
      ) {
        const additional = latestAdditionalRecordsTag(tags);
        if (additional) {
          reopenedDefault = additional;
        } else {
          additionalTargetIsAutoRef.current = true;
          setAdditionalTargetIsAuto(true);
          setAllowAdditionalUploads(true);
          reopenedDefault = null;
        }
      }

      setActiveTag((prev) => {
        if (preserveTag && tags.includes(preserveTag)) return preserveTag;
        if (folderExplicitRef.current && prev && tags.includes(prev)) return prev;
        if (additionalTargetIsAutoRef.current && !folderExplicitRef.current) return null;
        if (reopenedDefault !== undefined) return reopenedDefault;
        if (prev && tags.includes(prev)) return prev;
        return tags[0] || null;
      });
      setSelectedFileIds([]);
      setSelectedAll(false);
    } catch (err: any) {
      onToast(err.message || 'Failed to load files', 'error');
    } finally {
      setLoading(false);
    }
  }, [caseId, onToast]);

  useEffect(() => {
    setLoading(true);
    loadFiles();
  }, [loadFiles]);

  const activeRows = activeTag ? fileGroups[activeTag] || [] : [];
  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return activeRows;
    return activeRows.filter((r) =>
      String(r.original_name || r.name || '').toLowerCase().includes(q)
    );
  }, [activeRows, searchQuery]);

  const getUploadOptions = (forDelivery = false) => {
    if (forDelivery && deliveryType) {
      return {
        isMrrDelivery: deliveryType === 'mrr',
        isCaseDelivery: deliveryType === 'case',
        deliveryComment: uploadOptions.deliveryComment || undefined,
        deliveryFolderName: uploadOptions.deliveryFolderName.trim(),
      };
    }

    const base = {
      isMrrDelivery: false,
      isCaseDelivery: false,
      deliveryComment: undefined as string | undefined,
    };

    if (additionalTargetIsAutoRef.current) {
      return { ...base, isAdditionalUpload: true };
    }
    if (activeTag) {
      return { ...base, tag: activeTag };
    }
    return {
      ...base,
      isAdditionalUpload: allowAdditionalUploads || isDelivered || isClosed || isCompleted,
    };
  };

  const uploadFiles = async (files: File[], forDelivery = false) => {
    if (!files.length) return;
    if (forDelivery) {
      if (!canSubmitDelivery) {
        onToast('Choose delivery type, folder name, and at least one file.', 'warning');
        return;
      }
    } else if (!canUploadHere) {
      onToast(
        deliveryType
          ? 'Finish or cancel the delivery upload below before a normal upload.'
          : allowAdditionalUploads
            ? 'Select a folder or enable additional uploads to upload.'
            : 'Select a folder on the left before uploading.',
        'warning'
      );
      return;
    }

    try {
      setIsUploading(true);
      const progress: Record<string, number> = {};
      files.forEach((f) => { progress[f.name] = 0; });
      setUploadProgress(progress);

      const zipFiles = files.filter(
        (f) => f.type === 'application/zip' || f.name.toLowerCase().endsWith('.zip')
      );
      const regularFiles = files.filter(
        (f) => f.type !== 'application/zip' && !f.name.toLowerCase().endsWith('.zip')
      );
      const options = getUploadOptions(forDelivery);

      if (regularFiles.length > 0) {
        await caseService.uploadMultipleFiles(
          caseId,
          regularFiles,
          options,
          (fileName, fileProgress) =>
            setUploadProgress((prev) => ({ ...prev, [fileName]: fileProgress }))
        );
      }

      for (const file of zipFiles) {
        setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));
        await caseService.uploadZipFile(caseId, file, options, (p) =>
          setUploadProgress((prev) => ({ ...prev, [file.name]: p }))
        );
      }

      const deliveryFolderName = uploadOptions.deliveryFolderName.trim();
      const wasDeliveryUpload = forDelivery && deliveryType !== null;

      await loadFiles(activeTag);
      if (wasDeliveryUpload && deliveryFolderName) {
        const filesRes = await caseService.listFiles(caseId);
        const groups = filesRes.data.groups || {};
        const names = filesRes.data.tag_display_names || {};
        const tags = Object.keys(groups);
        const matchTag = tags.find(
          (t) => t === deliveryFolderName || names[t] === deliveryFolderName
        );
        if (matchTag) {
          setActiveTag(matchTag);
          setFileGroups(groups);
          setTagDisplayNames(names);
        }
      } else if (additionalTargetIsAutoRef.current && !useDeliveryTarget) {
        const filesRes = await caseService.listFiles(caseId);
        const tags = Object.keys(filesRes.data.groups || {});
        const additionalTag = latestAdditionalRecordsTag(tags);
        if (additionalTag) {
          folderExplicitRef.current = true;
          additionalTargetIsAutoRef.current = false;
          setAdditionalTargetIsAuto(false);
          setActiveTag(additionalTag);
          setFileGroups(filesRes.data.groups);
          setTagDisplayNames(filesRes.data.tag_display_names || {});
        }
      }

      const hasPdf = files.some(
        (f) =>
          f.name.toLowerCase().endsWith('.pdf') ||
          (f.type && f.type.toLowerCase() === 'application/pdf')
      );
      const hasZip = zipFiles.length > 0;

      if (wasDeliveryUpload) {
        setDeliveryType(null);
        setPendingDeliveryFiles([]);
        setSelectedExistingFileIds([]);
        setUploadOptions({ deliveryComment: '', deliveryFolderName: '' });
        setDeliveryExpanded(false);
        onCaseReopened?.();
      }

      onToast(
        hasPdf || hasZip
          ? `${files.length} file(s) uploaded. We're counting PDF pages and will notify you when done.`
          : `${files.length} file(s) uploaded successfully`,
        'success'
      );
    } catch (err: any) {
      onToast(err.message || 'Upload failed', 'error');
    } finally {
      setIsUploading(false);
      setUploadProgress({});
    }
  };

  /**
   * Unified delivery handler that supports:
   *  - copying existing MRR files to the delivery folder (no re-upload)
   *  - uploading new files as part of delivery
   *  - or both at once (existing files handled first to set status + notify, then new files stored in folder)
   */
  const deliverFiles = async () => {
    if (!canSubmitDelivery) return;
    const folderName = uploadOptions.deliveryFolderName.trim();
    const isMrr = deliveryType === 'mrr';
    const isCase = deliveryType === 'case';

    const zipFiles = pendingDeliveryFiles.filter(
      (f) => f.type === 'application/zip' || f.name.toLowerCase().endsWith('.zip')
    );
    const regularFiles = pendingDeliveryFiles.filter(
      (f) => f.type !== 'application/zip' && !f.name.toLowerCase().endsWith('.zip')
    );

    try {
      setIsUploading(true);
      setUploadProgress({});

      let deliveryHandled = false;

      // Step 1: copy existing MRR files to delivery folder (sets status + sends notification)
      if (selectedExistingFileIds.length > 0) {
        await caseService.deliverExistingFiles(caseId, {
          file_ids: selectedExistingFileIds,
          delivery_folder_name: folderName,
          is_case_delivery: isCase || undefined,
          is_mrr_delivery: isMrr || undefined,
          delivery_comment: uploadOptions.deliveryComment || undefined,
        });
        deliveryHandled = true;
      }

      // Step 2: upload new regular files
      if (regularFiles.length > 0) {
        const uploadOpts = deliveryHandled
          // Delivery already triggered — just store files in the folder without re-notifying
          ? { tag: folderName }
          : {
              isMrrDelivery: isMrr,
              isCaseDelivery: isCase,
              deliveryFolderName: folderName,
              deliveryComment: uploadOptions.deliveryComment || undefined,
            };
        await caseService.uploadMultipleFiles(
          caseId,
          regularFiles,
          uploadOpts,
          (fileName, p) => setUploadProgress((prev) => ({ ...prev, [fileName]: p }))
        );
        deliveryHandled = true;
      }

      // Step 3: upload ZIP files
      for (const file of zipFiles) {
        setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));
        const uploadOpts = deliveryHandled
          ? { tag: folderName }
          : {
              isMrrDelivery: isMrr,
              isCaseDelivery: isCase,
              deliveryFolderName: folderName,
              deliveryComment: uploadOptions.deliveryComment || undefined,
            };
        await caseService.uploadZipFile(
          caseId,
          file,
          uploadOpts,
          (p) => setUploadProgress((prev) => ({ ...prev, [file.name]: p }))
        );
        deliveryHandled = true;
      }

      // Refresh and navigate to delivery folder
      const filesRes = await caseService.listFiles(caseId);
      const groups = filesRes.data.groups || {};
      const names = filesRes.data.tag_display_names || {};
      const tags = Object.keys(groups);
      const matchTag = tags.find((t) => t === folderName || names[t] === folderName);
      setFileGroups(groups);
      setTagDisplayNames(names);
      if (matchTag) setActiveTag(matchTag);

      // Reset delivery state
      setDeliveryType(null);
      setPendingDeliveryFiles([]);
      setSelectedExistingFileIds([]);
      setUploadOptions({ deliveryComment: '', deliveryFolderName: '' });
      setDeliveryExpanded(false);
      onCaseReopened?.();

      const totalCount = selectedExistingFileIds.length + pendingDeliveryFiles.length;
      const hasPdf = pendingDeliveryFiles.some(
        (f) => f.name.toLowerCase().endsWith('.pdf') || (f.type && f.type.toLowerCase() === 'application/pdf')
      );
      const hasZip = zipFiles.length > 0;
      onToast(
        hasPdf || hasZip
          ? `${totalCount} file(s) delivered. We're counting PDF pages and will notify you when done.`
          : `${totalCount} file(s) delivered successfully`,
        'success'
      );
    } catch (err: any) {
      onToast(err.message || 'Delivery failed', 'error');
    } finally {
      setIsUploading(false);
      setUploadProgress({});
    }
  };

  const handleDownloadFile = async (file: CaseFileRow) => {
    try {
      setIsDownloading(true);
      setDownloadProgress(0);
      setDownloadingFileName(file.original_name || file.name);
      await caseService.downloadFile(
        caseId,
        file.id,
        file.original_name || file.name,
        (progress) => setDownloadProgress(progress)
      );
    } catch (err: any) {
      onToast(err.message || 'Download failed', 'error');
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
      setDownloadingFileName('');
    }
  };

  const handleDownloadSelected = async () => {
    if (!selectedFileIds.length) return;
    try {
      if (selectedFileIds.length === 1) {
        const file = activeRows.find((r) => r.id === selectedFileIds[0]);
        if (file) await handleDownloadFile(file);
        return;
      }
      setIsDownloading(true);
      setDownloadProgress(0);
      setDownloadingFileName(`case_${caseId}_files.zip`);
      const blob = await caseService.downloadFilesZip(
        caseId,
        selectedFileIds,
        `case_${caseId}_files`,
        (progress) => setDownloadProgress(progress)
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `case_${caseId}_files.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      onToast(err.message || 'Download failed', 'error');
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
      setDownloadingFileName('');
    }
  };

  const handleSaveTag = async (tag: string, displayName?: string) => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/cases/${caseId}/files/tag`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          tag,
          display_name: (displayName ?? '').trim() || null,
        }),
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message || 'Failed to update folder name');
      await loadFiles(tag);
      setEditingTag(null);
      setNewDisplayName('');
      onToast('Folder name updated', 'success');
    } catch (err: any) {
      onToast(err.message || 'Failed to update folder name', 'error');
      setEditingTag(null);
      setNewDisplayName('');
    }
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) {
      onToast('Enter a folder name', 'warning');
      return;
    }
    setCreatingFolder(true);
    try {
      const result = await caseService.createCaseFolder(caseId, name);
      const createdTag = result.data?.tag as string;
      folderExplicitRef.current = true;
      additionalTargetIsAutoRef.current = false;
      setAdditionalTargetIsAuto(false);
      await loadFiles(createdTag);
      setActiveTag(createdTag);
      setCreateFolderOpen(false);
      setNewFolderName('');
      onToast('Folder created', 'success');
    } catch (err: any) {
      onToast(err.message || 'Failed to create folder', 'error');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleHideFolder = async () => {
    if (!folderToHide) return;
    setHidingFolder(true);
    try {
      await caseService.hideCaseFolder(caseId, folderToHide.tag);
      const remainingTags = sortedTags.filter((t) => t !== folderToHide.tag);
      const nextTag = activeTag === folderToHide.tag ? (remainingTags[0] || null) : activeTag;
      await loadFiles(nextTag);
      if (activeTag === folderToHide.tag) {
        setActiveTag(nextTag);
      }
      onToast('Folder hidden. Files were kept in storage.', 'success');
      setHideFolderConfirmOpen(false);
      setFolderToHide(null);
    } catch (err: any) {
      onToast(err.message || 'Failed to hide folder', 'error');
    } finally {
      setHidingFolder(false);
    }
  };

  const handleReopen = () => {
    if (allowAdditionalUploads) {
      additionalTargetIsAutoRef.current = false;
      folderExplicitRef.current = true;
      setAdditionalTargetIsAuto(false);
      setAllowAdditionalUploads(false);
      setActiveTag((prev) => prev ?? (sortedTags[0] || null));
      return;
    }
    setSkipVendorReopen(false);
    setReopenDueDate(suggestedReopenDueDate());
    setReopenDialogOpen(true);
  };

  const handleConfirmReopen = async () => {
    if (!priorityBasedDueDate && !reopenDueDate) {
      onToast('Please choose a new due date', 'warning');
      return;
    }
    setReopening(true);
    try {
      await caseService.reopenCase(caseId, {
        skip_vendor_reopen: skipVendorReopen,
        ...(priorityBasedDueDate ? {} : { due_date: reopenDueDate }),
      });
      folderExplicitRef.current = false;
      additionalTargetIsAutoRef.current = true;
      setAdditionalTargetIsAuto(true);
      setAllowAdditionalUploads(true);
      await loadFiles();
      setReopenDialogOpen(false);
      onCaseReopened?.();
      onToast('Additional uploads enabled. New files will go to the Additional folder.', 'success');
    } catch (err: any) {
      onToast(err.message || 'Failed to enable additional uploads', 'error');
    } finally {
      setReopening(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (isUploading) return;
    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;
    if (deliveryType) {
      if (deliveryType === 'case' && deliverySource === 'sharepoint') return;
      setPendingDeliveryFiles((prev) => [...prev, ...files]);
      return;
    }
    if (!canUploadHere) return;
    uploadFiles(files, false);
  };

  const handleDeliveryTypeChange = (type: 'mrr' | 'case' | null) => {
    setDeliveryType(type);
    setSelectedExistingFileIds([]);
    setDeliverySource('files');
    setDeliverySharePointUrl('');
    if (!type) {
      setPendingDeliveryFiles([]);
      setUploadOptions({ deliveryComment: '', deliveryFolderName: '' });
      return;
    }
    const suggestions = type === 'mrr' ? mrrFolderSuggestions : caseFolderSuggestions;
    setUploadOptions((prev) => ({
      ...prev,
      deliveryFolderName: suggestions[0] || '',
      deliveryComment: type === 'case' ? prev.deliveryComment : '',
    }));
  };

  const useSharePointDelivery = deliveryType === 'case' && deliverySource === 'sharepoint';
  const canSubmitSharePointDelivery =
    useSharePointDelivery
    && !!uploadOptions.deliveryFolderName.trim()
    && /^https:\/\/\S+$/i.test(deliverySharePointUrl.trim())
    && !importingSharePoint
    && !isUploading;

  const deliverFromSharePoint = async () => {
    if (!canSubmitSharePointDelivery) return;
    try {
      setImportingSharePoint(true);
      const result = await caseService.importFromSharePoint(caseId, deliverySharePointUrl.trim(), {
        isCaseDelivery: true,
        deliveryFolderName: uploadOptions.deliveryFolderName.trim(),
        deliveryComment: uploadOptions.deliveryComment || undefined,
      });
      handleDeliveryTypeChange(null);
      setDeliveryExpanded(false);
      onToast(
        (result?.message || 'SharePoint import started.')
          + ' The case is marked Delivered once at least one file is copied.',
        'info'
      );
    } catch (err: any) {
      onToast(err.message || 'Failed to start SharePoint delivery', 'error');
    } finally {
      setImportingSharePoint(false);
    }
  };

  const handleImportFromSharePoint = async () => {
    const url = sharePointUrl.trim();
    if (!url) {
      onToast('Paste a SharePoint or OneDrive folder link.', 'warning');
      return;
    }
    if (!canUploadHere) {
      onToast(
        allowAdditionalUploads
          ? 'Select a folder or enable additional uploads before importing.'
          : 'Select a folder on the left before importing.',
        'warning'
      );
      return;
    }

    try {
      setImportingSharePoint(true);
      const options = getUploadOptions(false);
      const result = await caseService.importFromSharePoint(caseId, url, options);
      setSharePointDialogOpen(false);
      setSharePointUrl('');
      onToast(
        result?.message
          || 'SharePoint import started in the background. You will get an email when it finishes.',
        'info'
      );
    } catch (err: any) {
      onToast(err.message || 'Failed to start SharePoint import', 'error');
    } finally {
      setImportingSharePoint(false);
    }
  };

  const sortedTags = useMemo(() => [...groupNames], [groupNames]);

  const totalFileCount = useMemo(
    () => Object.values(fileGroups).reduce((sum, rows) => sum + rows.length, 0),
    [fileGroups]
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      {readOnly && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          View and download files shared for this case.
        </Typography>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '260px 1fr' },
          gap: 2,
          minHeight: 360,
        }}
      >
        {/* Folder sidebar — SharePoint-style nav */}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Folders
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {totalFileCount} file{totalFileCount !== 1 ? 's' : ''} total
              </Typography>
            </Box>
            {!readOnly && (
              <Button
                size="small"
                startIcon={<CreateNewFolderOutlinedIcon />}
                onClick={() => setCreateFolderOpen(true)}
              >
                New
              </Button>
            )}
          </Box>
          <Box sx={{ py: 0.5 }}>
            {sortedTags.map((tag) => {
              const count = (fileGroups[tag] || []).length;
              const displayName = getFolderDisplayName(tag, tagDisplayNames);
              const isActive = activeTag === tag;
              return (
                <Box
                  key={tag}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderLeft: '3px solid',
                    borderColor: isActive ? 'primary.main' : 'transparent',
                    bgcolor: isActive ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: isActive ? 'action.selected' : 'action.hover' },
                  }}
                >
                  <Button
                    size="small"
                    fullWidth
                    variant="text"
                    color="inherit"
                    onClick={() => {
                      folderExplicitRef.current = true;
                      additionalTargetIsAutoRef.current = false;
                      setAdditionalTargetIsAuto(false);
                      setActiveTag(tag);
                      setSelectedFileIds([]);
                      setSelectedAll(false);
                      setSearchQuery('');
                    }}
                    startIcon={isActive ? <FolderOpenIcon fontSize="small" color="primary" /> : <FolderIcon fontSize="small" />}
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      textTransform: 'none',
                      py: 1,
                      px: 1.5,
                      borderRadius: 0,
                    }}
                  >
                    <Box sx={{ overflow: 'hidden', width: '100%', textAlign: 'left' }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: isActive ? 600 : 400 }}>
                        {displayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {count} item{count !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Button>
                  {!readOnly && (
                    <Box sx={{ display: 'flex', mr: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingTag(tag);
                          setNewDisplayName(tagDisplayNames[tag] || '');
                        }}
                        aria-label="Rename folder"
                      >
                        ✎
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setFolderToHide({ tag, name: displayName, fileCount: count });
                          setHideFolderConfirmOpen(true);
                        }}
                        aria-label="Hide folder"
                      >
                        <VisibilityOffOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              );
            })}
            {sortedTags.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
                No folders yet.
              </Typography>
            )}
          </Box>
        </Box>

        {/* Main content — SharePoint-style file list */}
        <Box
          onDragOver={(e) => {
            if (!canUploadHere && !deliveryType) return;
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          sx={{
            border: '1px solid',
            borderColor: dragOver
              ? deliveryType
                ? 'warning.main'
                : 'primary.main'
              : deliveryType
                ? 'warning.light'
                : 'divider',
            borderRadius: 1,
            bgcolor: dragOver ? 'action.hover' : deliveryType ? 'warning.50' : 'background.paper',
            minHeight: 360,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Toolbar */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Box>
              <Breadcrumbs aria-label="folder path" sx={{ mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Case Files
                </Typography>
                {activeDisplayName ? (
                  <Typography variant="body2" color="text.primary" fontWeight={600}>
                    {activeDisplayName}
                  </Typography>
                ) : usesAutoAdditionalFolder ? (
                  <Typography variant="body2" color="text.primary" fontWeight={600}>
                    Additional records
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Select a folder
                  </Typography>
                )}
              </Breadcrumbs>
              {activeTag ? (
                <Typography variant="caption" color="text.secondary">
                  {activeRows.length} item{activeRows.length !== 1 ? 's' : ''}
                  {canUploadHere && selectedUploadFolderLabel
                    ? ` · Uploads go to "${selectedUploadFolderLabel}"`
                    : ''}
                </Typography>
              ) : usesAutoAdditionalFolder ? (
                <Typography variant="caption" color="text.secondary">
                  Uploads will use an empty additional folder or create one if needed
                </Typography>
              ) : null}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {canUploadHere && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length) uploadFiles(files);
                      if (e.target) e.target.value = '';
                    }}
                  />
                  <input
                    ref={zipInputRef}
                    type="file"
                    accept=".zip"
                    hidden
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length) uploadFiles(files);
                      if (e.target) e.target.value = '';
                    }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={isUploading ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
                    disabled={isUploading || (!activeTag && !allowAdditionalUploads)}
                    onClick={(e) => setUploadMenuAnchor(e.currentTarget)}
                  >
                    Upload
                  </Button>
                  <Menu
                    anchorEl={uploadMenuAnchor}
                    open={Boolean(uploadMenuAnchor)}
                    onClose={() => setUploadMenuAnchor(null)}
                  >
                    <MenuItem
                      onClick={() => {
                        setUploadMenuAnchor(null);
                        fileInputRef.current?.click();
                      }}
                    >
                      Upload files to this folder
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setUploadMenuAnchor(null);
                        zipInputRef.current?.click();
                      }}
                    >
                      Upload ZIP to this folder
                    </MenuItem>
                  </Menu>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={isUploading || importingSharePoint || (!activeTag && !allowAdditionalUploads)}
                    onClick={() => setSharePointDialogOpen(true)}
                  >
                    Import from SharePoint
                  </Button>
                </>
              )}
              {selectedFileIds.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  disabled={isDownloading}
                  onClick={handleDownloadSelected}
                >
                  Download
                </Button>
              )}
              {showSync && (hasVendorAssignment || hasClientAssignment) && (
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={async () => {
                    const message = hasVendorAssignment
                      ? 'Sync all files from this vendor case to the client case? Duplicates will be skipped.'
                      : 'Sync all files from the vendor case to this client case? Duplicates will be skipped.';
                    if (!window.confirm(message)) return;
                    try {
                      const response = await caseService.syncFilesToClient(caseId);
                      if (response.success) {
                        onToast(
                          `${response.message} (${response.synced_count} synced, ${response.skipped_count} skipped)`,
                          'success'
                        );
                        await loadFiles(activeTag);
                      }
                    } catch (err: any) {
                      onToast(err.message || 'Failed to sync files', 'error');
                    }
                  }}
                >
                  Sync
                </Button>
              )}
            </Box>
          </Box>

          <Box sx={{ px: 2, py: 1.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <TextField
            size="small"
            placeholder="Filter files in this folder..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            sx={{ mb: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />

          {/* Reopen / additional uploads — staff only */}
          {showReopenBanner && (
            <Box
              sx={{
                mb: 1.5,
                p: 2,
                borderRadius: 1,
                bgcolor: needsReopenToUpload ? 'warning.light' : 'info.light',
                border: '1px solid',
                borderColor: needsReopenToUpload ? 'warning.main' : 'info.main',
              }}
            >
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                {allowAdditionalUploads
                  ? 'Additional uploads are enabled. Select a folder, create a new one, or upload — an additional folder is created only when needed.'
                  : isClosed
                    ? 'This case is closed. Enable additional uploads to add more files.'
                    : isCompleted
                      ? 'This case is completed. Enable additional uploads to add more files.'
                      : isMrrDelivered
                        ? 'MRR delivered. Upload to any folder, or create a new one with the New button.'
                        : 'This case is delivered. Enable additional uploads to add more files.'}
              </Typography>

              <Button
                variant="contained"
                color={allowAdditionalUploads ? 'inherit' : needsReopenToUpload ? 'warning' : 'primary'}
                size="medium"
                fullWidth
                onClick={handleReopen}
                sx={{
                  ...(allowAdditionalUploads
                    ? { bgcolor: 'grey.600', color: 'white', '&:hover': { bgcolor: 'grey.700' } }
                    : {}),
                }}
              >
                {allowAdditionalUploads ? 'Disable Additional Uploads' : 'Enable Additional Uploads'}
              </Button>

              {allowAdditionalUploads && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Import doctor records from SharePoint into{' '}
                    {activeDisplayName || 'an Additional records folder'}
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="SharePoint / OneDrive folder link"
                      placeholder="https://....sharepoint.com/:f:/..."
                      value={sharePointUrl}
                      onChange={(e) => setSharePointUrl(e.target.value)}
                      disabled={importingSharePoint}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !importingSharePoint) {
                          e.preventDefault();
                          handleImportFromSharePoint();
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      size="medium"
                      disabled={importingSharePoint || !sharePointUrl.trim()}
                      onClick={handleImportFromSharePoint}
                      sx={{ whiteSpace: 'nowrap', minWidth: { sm: 120 } }}
                    >
                      {importingSharePoint ? 'Starting...' : 'Import'}
                    </Button>
                  </Stack>
                  {importingSharePoint && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="text.secondary">
                        Starting SharePoint import…
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Case / MRR delivery upload — staff only */}
          {!readOnly && deliveryFlagsApply && !isDelivered && !isClosed && !isCompleted && (
            <Paper
              variant="outlined"
              sx={{
                mb: 1.5,
                borderColor: deliveryType ? 'warning.main' : 'divider',
                bgcolor: deliveryType ? 'warning.50' : 'background.paper',
              }}
            >
              <Button
                fullWidth
                onClick={() => setDeliveryExpanded((v) => !v)}
                endIcon={deliveryExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ justifyContent: 'space-between', px: 2, py: 1.25, textTransform: 'none' }}
              >
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle2">Case delivery upload</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {deliveryType
                      ? 'Choose files, folder, then upload & deliver in one step'
                      : 'Optional — mark MRR or case delivery when uploading'}
                  </Typography>
                </Box>
              </Button>
              <Collapse in={deliveryExpanded}>
                <Box sx={{ px: 2, pb: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.75 }}>
                    1. Delivery type
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    color="primary"
                    value={deliveryType}
                    onChange={(_e, value: 'mrr' | 'case' | null) => {
                      handleDeliveryTypeChange(value);
                    }}
                    sx={{
                      mb: 1.5,
                      '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                        py: 0.75,
                        border: '1px solid',
                        borderColor: 'grey.400',
                        color: 'text.primary',
                        bgcolor: 'background.paper',
                        '&:hover': {
                          bgcolor: 'grey.100',
                        },
                        '&.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          borderColor: 'primary.main',
                          fontWeight: 600,
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                        },
                      },
                    }}
                  >
                    <ToggleButton value="mrr">MRR delivery</ToggleButton>
                    <ToggleButton value="case">Case delivery</ToggleButton>
                  </ToggleButtonGroup>
                  {deliveryType === 'mrr' && (
                    <Typography variant="caption" color="warning.dark" display="block" sx={{ mb: 1.5 }}>
                      Uploading will change case status to MRR Delivered.
                    </Typography>
                  )}
                  {deliveryType === 'case' && (
                    <Typography variant="caption" color="warning.dark" display="block" sx={{ mb: 1.5 }}>
                      Uploading will change case status to Delivered.
                    </Typography>
                  )}

                  {deliveryType && (
                    <>
                      <Divider sx={{ mb: 1.5 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.75 }}>
                        2. Delivery folder
                      </Typography>
                      <TextField
                        required
                        size="small"
                        fullWidth
                        label="Folder name"
                        value={uploadOptions.deliveryFolderName}
                        onChange={(e) =>
                          setUploadOptions((prev) => ({ ...prev, deliveryFolderName: e.target.value }))
                        }
                        error={!uploadOptions.deliveryFolderName.trim()}
                        helperText="Required — files will be stored in this folder"
                        sx={{ mb: 1, maxWidth: 520 }}
                      />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Suggestions:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                        {(deliveryType === 'mrr' ? mrrFolderSuggestions : caseFolderSuggestions).map((s) => (
                          <Chip
                            key={s}
                            label={s}
                            size="small"
                            variant={uploadOptions.deliveryFolderName === s ? 'filled' : 'outlined'}
                            color={uploadOptions.deliveryFolderName === s ? 'primary' : 'default'}
                            onClick={() =>
                              setUploadOptions((prev) => ({ ...prev, deliveryFolderName: s }))
                            }
                          />
                        ))}
                      </Box>

                      {deliveryType === 'case' && (
                        <TextField
                          size="small"
                          label="Delivery comment (optional)"
                          value={uploadOptions.deliveryComment}
                          onChange={(e) =>
                            setUploadOptions((prev) => ({ ...prev, deliveryComment: e.target.value }))
                          }
                          multiline
                          minRows={2}
                          fullWidth
                          sx={{ mb: 1.5, maxWidth: 520 }}
                        />
                      )}

                      <Divider sx={{ mb: 1.5 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.75 }}>
                        3. Files to deliver
                      </Typography>

                      {deliveryType === 'case' && (
                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          value={deliverySource}
                          onChange={(_e, value: 'files' | 'sharepoint' | null) => {
                            if (value) setDeliverySource(value);
                          }}
                          disabled={isUploading || importingSharePoint}
                          sx={{ mb: 1.5, '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5 } }}
                        >
                          <ToggleButton value="files">Upload files</ToggleButton>
                          <ToggleButton value="sharepoint">SharePoint link</ToggleButton>
                        </ToggleButtonGroup>
                      )}

                      {useSharePointDelivery && (
                        <Box sx={{ maxWidth: 520 }}>
                          <TextField
                            size="small"
                            fullWidth
                            label="SharePoint / OneDrive folder link"
                            placeholder="https://..."
                            value={deliverySharePointUrl}
                            onChange={(e) => setDeliverySharePointUrl(e.target.value)}
                            disabled={importingSharePoint}
                            error={!!deliverySharePointUrl.trim() && !/^https:\/\/\S+$/i.test(deliverySharePointUrl.trim())}
                            helperText="Every file in the shared folder is downloaded into the delivery folder above. This runs in the background and you get an email when it finishes. The case is marked Delivered only if at least one file is copied."
                            sx={{ mb: 1.5 }}
                          />
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Button
                              variant="contained"
                              color="warning"
                              disabled={!canSubmitSharePointDelivery}
                              startIcon={importingSharePoint ? <CircularProgress size={14} color="inherit" /> : undefined}
                              onClick={deliverFromSharePoint}
                            >
                              {importingSharePoint ? 'Starting...' : 'Import & deliver from SharePoint'}
                            </Button>
                            <Button
                              size="small"
                              color="inherit"
                              onClick={() => handleDeliveryTypeChange(null)}
                              disabled={importingSharePoint}
                            >
                              Cancel
                            </Button>
                          </Box>
                          {!uploadOptions.deliveryFolderName.trim() && (
                            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                              Enter a delivery folder name to continue.
                            </Typography>
                          )}
                        </Box>
                      )}

                      {!useSharePointDelivery && (<>
                      {/* Existing MRR records selector (Case delivery only) */}
                      {deliveryType === 'case' && mrrDeliveredFiles.length > 0 && (
                        <Box sx={{ mb: 1.5, maxWidth: 520 }}>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            Select from existing MRR records (no re-upload needed):
                          </Typography>
                          <Box
                            sx={{
                              border: '1px solid',
                              borderColor: selectedExistingFileIds.length > 0 ? 'primary.main' : 'divider',
                              borderRadius: 1,
                              maxHeight: 200,
                              overflow: 'auto',
                              bgcolor: 'background.paper',
                            }}
                          >
                            <List dense disablePadding>
                              {mrrDeliveredFiles.map((file) => (
                                <ListItem key={file.id} disableGutters sx={{ px: 1, py: 0.25 }}>
                                  <Checkbox
                                    size="small"
                                    checked={selectedExistingFileIds.includes(file.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedExistingFileIds((prev) => [...prev, file.id]);
                                      } else {
                                        setSelectedExistingFileIds((prev) => prev.filter((id) => id !== file.id));
                                      }
                                    }}
                                    sx={{ p: 0.5 }}
                                  />
                                  <ListItemText
                                    primary={file.original_name || file.name}
                                    secondary={`${file.size_label || ''} · ${file.tagDisplayName}`}
                                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                    secondaryTypographyProps={{ variant: 'caption' }}
                                    sx={{ ml: 0.5 }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                          {selectedExistingFileIds.length > 0 && (
                            <Typography variant="caption" color="primary.main" display="block" sx={{ mt: 0.5 }}>
                              {selectedExistingFileIds.length} existing file{selectedExistingFileIds.length !== 1 ? 's' : ''} selected
                            </Typography>
                          )}
                        </Box>
                      )}

                      <input
                        ref={deliveryFileInputRef}
                        type="file"
                        multiple
                        hidden
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          if (files.length) {
                            setPendingDeliveryFiles((prev) => [...prev, ...files]);
                          }
                          if (e.target) e.target.value = '';
                        }}
                      />
                      <Box
                        sx={{
                          border: '1px dashed',
                          borderColor: pendingDeliveryFiles.length ? 'divider' : (selectedExistingFileIds.length > 0 ? 'divider' : 'warning.main'),
                          borderRadius: 1,
                          p: 1.5,
                          mb: 1.5,
                          maxWidth: 520,
                          bgcolor: 'background.paper',
                        }}
                      >
                        {pendingDeliveryFiles.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {selectedExistingFileIds.length > 0
                              ? 'Optionally add new files to also include in this delivery.'
                              : 'No files selected yet. Add files below or drag them onto this panel.'}
                          </Typography>
                        ) : (
                          <List dense disablePadding sx={{ mb: 1 }}>
                            {pendingDeliveryFiles.map((file, idx) => (
                              <ListItem key={`${file.name}-${idx}`} disableGutters sx={{ py: 0.25 }}>
                                <ListItemText
                                  primary={file.name}
                                  secondary={`${(file.size / 1024).toFixed(1)} KB`}
                                  primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                  secondaryTypographyProps={{ variant: 'caption' }}
                                />
                                <ListItemSecondaryAction>
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    aria-label="Remove file"
                                    onClick={() =>
                                      setPendingDeliveryFiles((prev) => prev.filter((_, i) => i !== idx))
                                    }
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))}
                          </List>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => deliveryFileInputRef.current?.click()}
                        >
                          Add files
                        </Button>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button
                          variant="contained"
                          color="warning"
                          disabled={!canSubmitDelivery}
                          startIcon={isUploading ? <CircularProgress size={14} color="inherit" /> : undefined}
                          onClick={() => deliverFiles()}
                        >
                          {(() => {
                            const total = pendingDeliveryFiles.length + selectedExistingFileIds.length;
                            return `Upload & deliver (${total} file${total !== 1 ? 's' : ''})`;
                          })()}
                        </Button>
                        <Button
                          size="small"
                          color="inherit"
                          onClick={() => handleDeliveryTypeChange(null)}
                          disabled={isUploading}
                        >
                          Cancel
                        </Button>
                      </Box>
                      {!uploadOptions.deliveryFolderName.trim() && (pendingDeliveryFiles.length > 0 || selectedExistingFileIds.length > 0) && (
                        <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                          Enter a delivery folder name to continue.
                        </Typography>
                      )}
                      </>)}
                    </>
                  )}

                  {!deliveryType && (
                    <Typography variant="body2" color="text.secondary">
                      Select MRR or Case delivery above to choose a folder and files together.
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Paper>
          )}

          {!activeTag && canUpload && !readOnly && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, textAlign: 'center', py: 2 }}>
              Select a folder on the left to view or upload files.
            </Typography>
          )}

          {/* File table */}
          <Box sx={{ overflowX: 'auto', flex: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell width={40} padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedAll}
                      indeterminate={
                        selectedFileIds.length > 0 && selectedFileIds.length < filteredRows.length
                      }
                      onChange={(_e, checked) => {
                        setSelectedAll(checked);
                        setSelectedFileIds(checked ? filteredRows.map((r) => r.id) : []);
                      }}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Modified</TableCell>
                  <TableCell>Size</TableCell>
                  {!readOnly && <TableCell>Modified by</TableCell>}
                  <TableCell align="right" width={readOnly ? 80 : 140} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((r) => {
                  const fileName = r.original_name || r.name;
                  const isPdf =
                    r.mime_type === 'application/pdf' ||
                    fileName.toLowerCase().endsWith('.pdf');
                  return (
                    <TableRow key={r.id} hover selected={selectedFileIds.includes(r.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedFileIds.includes(r.id)}
                          onChange={(_e, checked) => {
                            setSelectedFileIds((prev) => {
                              const next = new Set(prev);
                              if (checked) next.add(r.id);
                              else next.delete(r.id);
                              return Array.from(next);
                            });
                            setSelectedAll(false);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <InsertDriveFileOutlinedIcon fontSize="small" color="action" />
                          <MLink
                            component="button"
                            underline="hover"
                            onClick={() => handleDownloadFile(r)}
                            sx={{
                              textAlign: 'left',
                              border: 'none',
                              background: 'none',
                              cursor: 'pointer',
                              p: 0,
                              font: 'inherit',
                            }}
                          >
                            {fileName}
                          </MLink>
                        </Box>
                      </TableCell>
                      <TableCell>{r.created_at ? formatDateTime(r.created_at) : '—'}</TableCell>
                      <TableCell>{r.size_label || '—'}</TableCell>
                      {!readOnly && (
                        <TableCell>{r.uploaded_by_user?.name || '—'}</TableCell>
                      )}
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <IconButton
                          size="small"
                          title="Download"
                          onClick={() => handleDownloadFile(r)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                        {showDocumentReview && isPdf && (
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ ml: 0.5 }}
                            onClick={() => navigateToReview(r.id)}
                          >
                            Review
                          </Button>
                        )}
                        {!readOnly && (
                          <IconButton
                            size="small"
                            color="error"
                            title="Delete"
                            onClick={() => {
                              setFileToDelete({ id: r.id, name: fileName });
                              setDeleteConfirmOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={readOnly ? 5 : 6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      {!activeTag
                        ? 'Select a folder to view its files.'
                        : searchQuery
                          ? 'No files match your filter.'
                          : deliveryType
                            ? 'Use the delivery panel above to add files, or drag files here.'
                            : canUploadHere
                              ? 'This folder is empty. Drag files here or click Upload.'
                              : 'This folder is empty.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          {isUploading && Object.keys(uploadProgress).length > 0 && (
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {deliveryType
                  ? `Delivering to "${uploadOptions.deliveryFolderName.trim()}"...`
                  : `Uploading to ${selectedUploadFolderLabel}...`}
              </Typography>
              {Object.entries(uploadProgress).map(([name, pct]) => (
                <Box key={name} sx={{ mb: 1 }}>
                  <Typography variant="caption" noWrap display="block">{name}</Typography>
                  <LinearProgress variant="determinate" value={pct} />
                </Box>
              ))}
            </Box>
          )}
          </Box>
        </Box>
      </Box>

      {/* SharePoint link import */}
      <Dialog
        open={sharePointDialogOpen}
        onClose={() => !importingSharePoint && setSharePointDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Import from SharePoint</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste a SharePoint or OneDrive <strong>folder</strong> sharing link.
            Files will be copied into
            {activeDisplayName ? (
              <> <strong>&quot;{activeDisplayName}&quot;</strong></>
            ) : (
              ' the selected folder'
            )}{' '}
            in the background.
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            You can close this page after submitting. When the import finishes, we will email the person who started it.
            Nested folders are included. The link should open in an incognito window without signing in.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="SharePoint / OneDrive folder link"
            placeholder="https://....sharepoint.com/:f:/..."
            value={sharePointUrl}
            onChange={(e) => setSharePointUrl(e.target.value)}
            disabled={importingSharePoint}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !importingSharePoint) handleImportFromSharePoint();
            }}
          />
          {importingSharePoint && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Starting background import…
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSharePointDialogOpen(false);
              setSharePointUrl('');
            }}
            disabled={importingSharePoint}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleImportFromSharePoint}
            disabled={importingSharePoint || !sharePointUrl.trim()}
          >
            {importingSharePoint ? 'Starting...' : 'Start import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create folder dialog */}
      <Dialog open={createFolderOpen} onClose={() => !creatingFolder && setCreateFolderOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New folder</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a folder to organize case files. You can upload files into it after creating.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Folder name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            disabled={creatingFolder}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateFolder();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderOpen(false)} disabled={creatingFolder}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateFolder} disabled={creatingFolder || !newFolderName.trim()}>
            {creatingFolder ? 'Creating...' : 'Create folder'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reopenDialogOpen}
        onClose={() => !reopening && setReopenDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{priorityBasedDueDate ? 'Reopen case' : 'Set a new due date'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {priorityBasedDueDate
              ? `Reopening this case for additional records. The due date will be set from the ${casePriority || 'normal'} priority.`
              : 'Reopening this case for additional records. Choose a new due date so nearing-due alerts use the updated date.'}
            {currentDueDate
              ? ` Current due date: ${new Date(`${String(currentDueDate).slice(0, 10)}T12:00:00`).toLocaleDateString()}.`
              : ' No due date is currently set.'}
          </DialogContentText>
          {!priorityBasedDueDate && (
          <TextField
            autoFocus
            fullWidth
            type="date"
            label="New due date"
            value={reopenDueDate}
            onChange={(e) => setReopenDueDate(e.target.value)}
            required
            InputLabelProps={{ shrink: true }}
            helperText="Suggested: 15 days from today"
            disabled={reopening}
          />
          )}
          {hasVendorAssignment && (
            <FormControlLabel
              sx={{ display: 'block', mt: 1.5 }}
              control={
                <Checkbox
                  checked={skipVendorReopen}
                  onChange={(e) => setSkipVendorReopen(e.target.checked)}
                  size="small"
                  disabled={reopening}
                />
              }
              label={<Typography variant="body2">Skip vendor case reopen</Typography>}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenDialogOpen(false)} disabled={reopening}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmReopen}
            disabled={reopening || (!priorityBasedDueDate && !reopenDueDate)}
          >
            {reopening ? 'Reopening...' : 'Reopen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hide folder confirmation */}
      <Dialog
        open={hideFolderConfirmOpen}
        onClose={() => !hidingFolder && setHideFolderConfirmOpen(false)}
      >
        <DialogTitle>Hide folder</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Hide &quot;{folderToHide?.name}&quot; from the file browser?
            {folderToHide && folderToHide.fileCount > 0
              ? ` This folder contains ${folderToHide.fileCount} file(s). Files will stay in storage but won't be shown.`
              : ' This only removes the folder from view.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setHideFolderConfirmOpen(false); setFolderToHide(null); }} disabled={hidingFolder}>
            Cancel
          </Button>
          <Button color="warning" variant="contained" onClick={handleHideFolder} disabled={hidingFolder}>
            {hidingFolder ? 'Hiding...' : 'Hide folder'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename folder dialog */}
      <Dialog open={!!editingTag} onClose={() => { setEditingTag(null); setNewDisplayName(''); }} maxWidth="xs" fullWidth>
        <DialogTitle>Rename folder</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Internal name: {editingTag}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Display name"
            value={newDisplayName}
            onChange={(e) => setNewDisplayName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && editingTag) handleSaveTag(editingTag, newDisplayName);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditingTag(null); setNewDisplayName(''); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => editingTag && handleSaveTag(editingTag, newDisplayName)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setFileToDelete(null); }}
      >
        <DialogTitle>Delete file</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete &quot;{fileToDelete?.name}&quot;? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteConfirmOpen(false); setFileToDelete(null); }}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              if (!fileToDelete) return;
              try {
                await caseService.deleteFile(caseId, fileToDelete.id);
                await loadFiles(activeTag);
                onToast('File deleted', 'success');
              } catch (err: any) {
                onToast(err.message || 'Delete failed', 'error');
              } finally {
                setDeleteConfirmOpen(false);
                setFileToDelete(null);
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Download progress */}
      <Dialog open={isDownloading} maxWidth="sm" fullWidth disableEscapeKeyDown onClose={() => {}}>
        <DialogTitle>Downloading</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>{downloadingFileName}</Typography>
          <LinearProgress variant="determinate" value={downloadProgress} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CaseFilesPanel;
